import React from 'react'
import {
  Box,
  Typography,
  Button,
  Link,
  Paper,
  Chip,
  Divider,
  Stack
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  Web,
  SportsHockey,
  OpenInNew,
  ContentCopy,
  Settings
} from '@mui/icons-material'

interface StepItem {
  title: string
  description: string
  hint?: string
  bullets?: string[]
  icon: React.ReactNode
}

const steps: StepItem[] = [
  {
    title: 'Sign in to Yahoo Fantasy',
    description: 'Open Yahoo Fantasy Sports and sign in with your Yahoo account.',
    icon: <Web fontSize="small" />
  },
  {
    title: 'Open your hockey league',
    description: 'Go to Fantasy > Hockey, then select your league from the list.',
    icon: <SportsHockey fontSize="small" />
  },
  {
    title: 'Copy your League ID from the URL',
    description: 'On your league home page, copy the numbers after /hockey/ in the address bar.',
    hint: 'Example URL: https://.../hockey/12345 (League ID: 12345)',
    icon: <ContentCopy fontSize="small" />
  },
  {
    title: 'Alternative: League settings',
    description: 'You can also find the League ID in league settings.',
    bullets: [
      'From league home, open Commissioner or League Settings.',
      'Look for League Information to find the League ID.'
    ],
    icon: <Settings fontSize="small" />
  }
]

const YahooFantasyStepsToConnect: React.FC = (): JSX.Element => {
  const theme = useTheme()

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.info.light, 0.25)}`,
        background: `linear-gradient(160deg, ${alpha(theme.palette.info.dark, 0.2)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 55%, ${alpha(theme.palette.success.dark, 0.12)} 100%)`,
        p: { xs: 2, sm: 3 }
      }}
    >
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <SportsHockey sx={{ color: 'info.light', mt: 0.3 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
              Find your Yahoo Fantasy Hockey League ID
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Follow these quick steps, then paste the League ID below.
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Stack spacing={1.25}>
          {steps.map((step, index) => (
            <Paper
              key={step.title}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
                p: { xs: 1.5, sm: 2 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Chip
                  size="small"
                  label={index + 1}
                  color="info"
                  icon={step.icon as React.ReactElement}
                  sx={{
                    mt: 0.2,
                    '& .MuiChip-label': { px: 1, fontWeight: 700 }
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>
                    {step.description}
                  </Typography>

                  {step.hint !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'inline-block',
                        mt: 1,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        color: 'warning.light',
                        bgcolor: alpha(theme.palette.warning.main, 0.15)
                      }}
                    >
                      {step.hint}
                    </Typography>
                  )}

                  {step.bullets !== undefined && (
                    <Box component="ul" sx={{ my: 1, pl: 2.4 }}>
                      {step.bullets.map((item) => (
                        <Typography key={item} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>

        <Button
          startIcon={<OpenInNew />}
          endIcon={<Web />}
          variant="contained"
          color="success"
          fullWidth
          href="https://sports.yahoo.com/fantasy"
          target="_blank"
          rel="noopener"
          sx={{
            fontWeight: 700,
            minHeight: 46,
            textTransform: 'none'
          }}
        >
          Open Yahoo Fantasy
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          Need help? Start at{' '}
          <Link href="https://sports.yahoo.com/fantasy" target="_blank" rel="noopener" underline="hover">
            sports.yahoo.com/fantasy
          </Link>
        </Typography>
      </Stack>
    </Paper>
  )
}

export default React.memo(YahooFantasyStepsToConnect)
